import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  Tag,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  message,
  Modal,
  Form,
  Tooltip,
  Drawer,
  Descriptions,
  Divider,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  SearchOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../../services';
import { formatCurrency, formatDate, debounce } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const InvoiceImportList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    statusId: null,
    dateFrom: null,
    dateTo: null,
    keyword: '',
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Action modal states
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionInvoice, setActionInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionForm] = Form.useForm();
  
  const navigate = useNavigate();

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug: Check token on component mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    //console.log('🔐 Current accessToken in localStorage:', token ? (token.substring(0, 20) + '...') : 'NULL');
    
    if (!token) {
      //console.warn('⚠️ No accessToken found! Setting test token...');
      // Temporarily set a token for testing (remove in production)
      localStorage.setItem('accessToken', '312c84a1-8e4f-11f0-8ae3-d4bed94fcc32');
      //console.log('✅ Test token set successfully');
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        offset: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        keyword: filters.keyword, // Maps to codeFilter
        supplierKeyword: filters.supplierKeyword, // Maps to supplierFilter
        status: filters.status, // Maps to statusFilter
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      };

      //console.log('📋 Loading invoices with params:', params);

      const response = await invoiceService.import.getAll(params);
      if (response.success) {
        setInvoices(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || (response.data ? response.data.length : 0),
        }));
      } else {
        message.error(response.message || 'Không thể tải danh sách hóa đơn');
        setInvoices([]);
      }
    } catch (error) {
      //console.error('💥 Error loading invoices:', error);
      
      if (error.message.includes('AccessToken not found')) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
        // Có thể redirect về login page
        // navigate('/login');
      } else if (error.response) {
        //console.error('📛 API Error response:', error.response.data);
        message.error(`Lỗi API: ${error.response.data.message || error.response.statusText}`);
      } else {
        message.error('Có lỗi xảy ra khi tải danh sách hóa đơn: ' + error.message);
      }
      
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = debounce((value) => {
    //console.log('🔍 Search filter changed to:', value);
    const newFilters = { ...filters, keyword: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
  }, 500);

  const handleDateRangeChange = (dates) => {
    //console.log('📅 Date filter changed to:', dates);
    let newFilters = { ...filters };
    
    if (dates && dates.length === 2) {
      newFilters.dateFrom = dates[0].format('YYYY-MM-DD');
      newFilters.dateTo = dates[1].format('YYYY-MM-DD');
    } else {
      newFilters.dateFrom = null;
      newFilters.dateTo = null;
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (status) => {
    //console.log('🏷️ Status filter changed to:', status);
    
    // Map UI values to exact API values
    let apiStatus = null;
    if (status === 'pending') apiStatus = 'Chưa giải quyết';
    else if (status === 'completed') apiStatus = 'hoàn thành';
    else if (status === 'cancelled') apiStatus = 'Đã hủy bỏ';
    
    //console.log('📤 Sending exact API status:', apiStatus);
    const newFilters = { ...filters, status: apiStatus };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleViewDetail = async (invoice) => {
    setDetailLoading(true);
    setInvoiceDetails([]); // Reset data cũ
    setSelectedInvoice(null);
    
    try {
      //console.log('📋 Loading detail for invoice:', invoice);
      
      // Lưu thông tin tổng quan của hóa đơn từ bảng
      setSelectedInvoice(invoice);
      setDetailVisible(true);
      
      // Gọi API để lấy chi tiết sản phẩm
      const response = await invoiceService.import.getById(invoice.id);
      //console.log('📦 API Response:', response);
      
      if (response.success && response.data) {
        // Kiểm tra nếu data là array hoặc object chứa array
        let detailsArray = [];
        if (Array.isArray(response.data)) {
          detailsArray = response.data;
        } else if (response.data.details && Array.isArray(response.data.details)) {
          detailsArray = response.data.details;
        } else if (response.data.products && Array.isArray(response.data.products)) {
          detailsArray = response.data.products;
        }
        
        //console.log('✅ Invoice details loaded:', detailsArray);
        setInvoiceDetails(detailsArray);
      } else {
        //console.warn('⚠️ No data found in response or success=false');
        message.error('Không thể tải chi tiết hóa đơn: ' + (response.message || 'Không có dữ liệu'));
        setInvoiceDetails([]);
      }
    } catch (error) {
      //console.error('💥 Error loading invoice detail:', error);
      message.error('Có lỗi xảy ra khi tải chi tiết hóa đơn');
    } finally {
      setDetailLoading(false);
    }
  };

  // Action handlers for approve, cancel, cancel-completed
  const handleActionClick = (type, invoice) => {
    setActionType(type);
    setActionInvoice(invoice);
    setActionModalVisible(true);
    actionForm.resetFields();
  };

  const handleActionSubmit = async () => {
    try {
      const values = await actionForm.validateFields();
      setActionLoading(true);

      const actionNames = {
        'approve': 'duyệt',
        'cancel': 'hủy',
        'cancel-completed': 'hủy hoàn thành'
      };

      let response;
      const note = values.note || '';
      
      switch (actionType) {
        case 'approve':
          response = await invoiceService.import.approve(actionInvoice.id, note);
          break;
        case 'cancel':
          response = await invoiceService.import.cancel(actionInvoice.id, note);
          break;
        case 'cancel-completed':
          response = await invoiceService.import.cancelCompleted(actionInvoice.id, note);
          break;
        default:
          throw new Error('Invalid action type');
      }

      if (response.success) {
        message.success(`${actionNames[actionType].charAt(0).toUpperCase() + actionNames[actionType].slice(1)} hóa đơn thành công`);
        setActionModalVisible(false);
        loadInvoices();
      } else {
        message.error(response.message || `Có lỗi xảy ra khi ${actionNames[actionType]} hóa đơn`);
      }
    } catch (error) {
      //console.error(`Error ${actionType} invoice:`, error);
      if (error.response?.status === 409) {
        message.error('Không thể thực hiện thao tác này với trạng thái hiện tại của hóa đơn');
      } else {
        message.error(`Có lỗi xảy ra khi thực hiện thao tác`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleActionCancel = () => {
    setActionModalVisible(false);
    setActionType('');
    setActionInvoice(null);
    actionForm.resetFields();
  };

  // Render mobile card layout
  const renderMobileCard = (invoice) => {
    const status = (invoice.status || '').toString().toLowerCase();
    const isPending = status === 'pending' || status === 'chưa giải quyết';
    const isCompleted = status === 'completed' || status === 'hoàn thành';
    
    const statusConfig = {
      'pending': { name: 'Chưa giải quyết', color: 'warning' },
      'completed': { name: 'Hoàn thành', color: 'success' },
      'cancelled': { name: 'Đã hủy bỏ', color: 'error' },
      'Chưa giải quyết': { name: 'Chưa giải quyết', color: 'warning' },
      'hoàn thành': { name: 'Hoàn thành', color: 'success' },
      'Đã hủy bỏ': { name: 'Đã hủy bỏ', color: 'error' },
      'approved': { name: 'Đã duyệt', color: 'success' },
      'rejected': { name: 'Từ chối', color: 'error' },
      'active': { name: 'Hoạt động', color: 'green' }
    };
    
    const statusInfo = statusConfig[invoice.status] || { name: invoice.status || 'N/A', color: 'default' };

    return (
      <Card
        key={invoice.id}
        size="small"
        style={{
          marginBottom: 12,
          borderRadius: 8,
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
        }}
        bodyStyle={{ padding: '12px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#1890ff', marginBottom: 4 }}>
              {invoice.code || `#${invoice.id}`}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
              {formatDate(invoice.created_at)}
            </div>
          </div>
          <Tag color={statusInfo.color} style={{ marginLeft: 8 }}>
            {statusInfo.name}
          </Tag>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: 2 }}>
            <strong>Nhà cung cấp:</strong> {invoice.supplier_name || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
            <strong>Người tạo:</strong> ID {invoice.created_by}
          </div>
          {invoice.note && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
              <strong>Ghi chú:</strong> {invoice.note.length > 50 ? invoice.note.substring(0, 50) + '...' : invoice.note}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(invoice)}
          >
            Xem chi tiết
          </Button>
          <Space size="small">
            {isPending && (
              <>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/invoices/import/edit/${invoice.id}`)}
                >
                  Sửa
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleActionClick('approve', invoice)}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Duyệt
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleActionClick('cancel', invoice)}
                >
                  Hủy
                </Button>
              </>
            )}
            {isCompleted && (
              <Button
                size="small"
                icon={<StopOutlined />}
                onClick={() => handleActionClick('cancel-completed', invoice)}
                style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
              >
                Hủy HT
              </Button>
            )}
          </Space>
        </div>
      </Card>
    );
  };

  const columns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code, record) => (
        <Space direction="vertical" size={0}>
          <Button 
            type="link" 
            style={{ padding: 0, fontSize: '14px', fontWeight: 'bold' }}
            onClick={() => handleViewDetail(record)}
          >
            {code || `#${record.id}`}
          </Button>
          <small style={{ color: '#999' }}>{formatDate(record.created_at)}</small>
        </Space>
      ),
    },
    {
      title: 'Nhà cung cấp',
      key: 'supplier',
      width: 200,
      render: (_, record) => (
        <div style={{ fontWeight: 'bold' }}>{record.supplier_name || 'N/A'}</div>
      ),
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by',
      key: 'created_by', 
      width: 120,
      render: (createdBy) => (
        <div>ID: {createdBy}</div>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      ellipsis: true,
      render: (note) => (
        <Tooltip title={note}>
          <div style={{ maxWidth: '150px' }}>
            {note || 'Không có ghi chú'}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        // Map API status to display
        const statusConfig = {
          'pending': { name: 'Chưa giải quyết', color: 'warning' },
          'completed': { name: 'Hoàn thành', color: 'success' },
          'cancelled': { name: 'Đã hủy bỏ', color: 'error' },
          // Keep display text mapping for backward compatibility
          'Chưa giải quyết': { name: 'Chưa giải quyết', color: 'warning' },
          'hoàn thành': { name: 'Hoàn thành', color: 'success' },
          'Đã hủy bỏ': { name: 'Đã hủy bỏ', color: 'error' },
          // Fallback cho các trạng thái cũ nếu có
          'approved': { name: 'Đã duyệt', color: 'success' },
          'rejected': { name: 'Từ chối', color: 'error' },
          'active': { name: 'Hoạt động', color: 'green' }
        };
        
        const statusInfo = statusConfig[status] || { name: status || 'N/A', color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.name}</Tag>;
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        // Normalize status to handle both API and display values
        const status = (record.status || '').toString().toLowerCase();
        const isPending = status === 'pending' || status === 'chưa giải quyết';
        const isCompleted = status === 'completed' || status === 'hoàn thành';
        
        return (
          <Space size="small">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            <Tooltip title={isPending ? 'Chỉnh sửa' : 'Chỉ cho phép chỉnh sửa khi trạng thái là "Chưa giải quyết"'}>
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => isPending && navigate(`/invoices/import/edit/${record.id}`)}
                disabled={!isPending}
              />
            </Tooltip>
            {isPending && (
              <Tooltip title="Duyệt hóa đơn">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  size="small"
                  style={{ color: '#52c41a' }}
                  onClick={() => handleActionClick('approve', record)}
                />
              </Tooltip>
            )}
            {isPending && (
              <Tooltip title="Hủy hóa đơn">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  size="small"
                  danger
                  onClick={() => handleActionClick('cancel', record)}
                />
              </Tooltip>
            )}
            {isCompleted && (
              <Tooltip title="Hủy hoàn thành">
                <Button
                  type="text"
                  icon={<StopOutlined />}
                  size="small"
                  style={{ color: '#fa8c16' }}
                  onClick={() => handleActionClick('cancel-completed', record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Card>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0
        }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            📋 Danh sách hóa đơn nhập
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/invoices/import/add')}
            size={isMobile ? 'middle' : 'default'}
            block={isMobile}
          >
            Tạo hóa đơn nhập
          </Button>
        </div>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Tìm kiếm theo mã, nhà cung cấp..."
              prefix={<SearchOutlined />}
              allowClear
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Lọc theo trạng thái"
              style={{ width: '100%' }}
              allowClear
              onChange={handleStatusFilter}
            >
              <Option value="pending">
                <Tag color="warning">Chưa giải quyết</Tag>
              </Option>
              <Option value="completed">
                <Tag color="success">Hoàn thành</Tag>
              </Option>
              <Option value="cancelled">
                <Tag color="error">Đã hủy bỏ</Tag>
              </Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Từ ngày', 'Đến ngày']}
              onChange={handleDateRangeChange}
            />
          </Col>
        </Row>

        {/* Mobile Card Layout or Desktop Table */}
        {isMobile ? (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>Đang tải danh sách...</div>
              </div>
            ) : invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <div>Không có hóa đơn nào</div>
              </div>
            ) : (
              <>
                {invoices.map(invoice => renderMobileCard(invoice))}
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
                    Hiển thị {Math.min(pagination.current * pagination.pageSize, pagination.total)} / {pagination.total} hóa đơn
                  </div>
                  <Space>
                    <Button
                      size="small"
                      disabled={pagination.current === 1}
                      onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                    >
                      Trang trước
                    </Button>
                    <Button
                      size="small"
                      disabled={pagination.current * pagination.pageSize >= pagination.total}
                      onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                    >
                      Trang sau
                    </Button>
                  </Space>
                </div>
              </>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} hóa đơn`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      {/* Chi tiết hóa đơn */}
      <Drawer
        title="📋 Chi tiết hóa đơn nhập"
        placement="right"
        size={isMobile ? 'default' : 'large'}
        width={isMobile ? '100%' : '60%'}
        onClose={() => {
          setDetailVisible(false);
          setSelectedInvoice(null);
          setInvoiceDetails([]);
          setDetailLoading(false);
        }}
        open={detailVisible}
        style={isMobile ? { top: 0 } : {}}
      >
        {selectedInvoice && (
          <div>
            <Descriptions 
              title="Thông tin hóa đơn" 
              column={isMobile ? 1 : 2} 
              bordered 
              size={isMobile ? 'small' : 'default'}
            >
              <Descriptions.Item label="Mã hóa đơn">
                {selectedInvoice.code || `#${selectedInvoice.id}`}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo">
                {formatDate(selectedInvoice.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Nhà cung cấp">
                {selectedInvoice.supplier_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Người tạo">
                ID: {selectedInvoice.created_by}
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                {selectedInvoice.status && (
                  (() => {
                    const statusConfig = {
                      'pending': { name: 'Chưa giải quyết', color: 'warning' },
                      'completed': { name: 'Hoàn thành', color: 'success' },
                      'cancelled': { name: 'Đã hủy bỏ', color: 'error' },
                      // Keep display text mapping for backward compatibility
                      'Chưa giải quyết': { name: 'Chưa giải quyết', color: 'warning' },
                      'hoàn thành': { name: 'Hoàn thành', color: 'success' },
                      'Đã hủy bỏ': { name: 'Đã hủy bỏ', color: 'error' },
                      // Fallback cho các trạng thái cũ nếu có
                      'approved': { name: 'Đã duyệt', color: 'success' },
                      'rejected': { name: 'Từ chối', color: 'error' },
                      'active': { name: 'Hoạt động', color: 'green' }
                    };
                    const statusInfo = statusConfig[selectedInvoice.status] || { name: selectedInvoice.status, color: 'default' };
                    return <Tag color={statusInfo.color}>{statusInfo.name}</Tag>;
                  })()
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Tổng tiền">
                <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
                  {invoiceDetails.length > 0 ? formatCurrency(
                    invoiceDetails.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0)
                  ) : 'Đang tính toán...'}
                </div>
              </Descriptions.Item>
              {selectedInvoice.note && (
                <Descriptions.Item label="Ghi chú" span={2}>
                  {selectedInvoice.note}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />
            <Title level={5}>Chi tiết sản phẩm</Title>
            
            {detailLoading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', color: '#666' }}>
                  Đang tải chi tiết sản phẩm...
                </div>
              </div>
            ) : invoiceDetails && invoiceDetails.length > 0 ? (
                <Table
                  dataSource={invoiceDetails}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={isMobile ? { x: 600 } : undefined}
                  columns={[
                    {
                      title: 'SKU',
                      dataIndex: 'sku',
                      key: 'sku',
                      width: isMobile ? 120 : 150,
                      render: (sku) => (
                        <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                          {sku}
                        </code>
                      )
                    },
                    {
                      title: 'ID Sản phẩm',
                      dataIndex: 'product_id',
                      key: 'product_id',
                      width: 100,
                      align: 'center',
                    },
                    {
                      title: 'ID Biến thể',
                      dataIndex: 'variant_id',
                      key: 'variant_id', 
                      width: 100,
                      align: 'center',
                    },
                    {
                      title: 'Số lượng',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      width: 80,
                      align: 'center',
                      render: (quantity) => (
                        <Tag color="blue">{quantity}</Tag>
                      )
                    },
                    {
                      title: 'Giá nhập',
                      dataIndex: 'price',
                      key: 'price',
                      width: 120,
                      align: 'right',
                      render: (price) => (
                        <div style={{ fontWeight: '500' }}>
                          {formatCurrency(parseFloat(price))}
                        </div>
                      )
                    },
                    {
                      title: 'Giá bán gốc',
                      dataIndex: 'variant_price',
                      key: 'variant_price',
                      width: 120,
                      align: 'right',
                      render: (price) => (
                        <div style={{ color: '#666' }}>
                          {formatCurrency(parseFloat(price))}
                        </div>
                      )
                    },
                    {
                      title: 'Thành tiền',
                      key: 'total',
                      width: 130,
                      align: 'right',
                      render: (_, record) => (
                        <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                          {formatCurrency(parseFloat(record.price) * record.quantity)}
                        </div>
                      )
                    },
                  ]}
                  summary={(pageData) => {
                    let totalAmount = 0;
                    let totalQuantity = 0;
                    
                    pageData.forEach(({ quantity, price }) => {
                      totalQuantity += quantity;
                      totalAmount += parseFloat(price) * quantity;
                    });

                    return (
                      <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                        <Table.Summary.Cell colSpan={3}>
                          <div style={{ fontWeight: 'bold' }}>Tổng cộng</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="center">
                          <Tag color="green" style={{ fontWeight: 'bold' }}>
                            {totalQuantity}
                          </Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell colSpan={2}></Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
                            {formatCurrency(totalAmount)}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
            ) : (
              <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                <div>Không có chi tiết sản phẩm</div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Action Modal */}
      <Modal
        title={(() => {
          const titles = {
            'approve': 'Duyệt hóa đơn',
            'cancel': 'Hủy hóa đơn',
            'cancel-completed': 'Hủy hoàn thành hóa đơn'
          };
          return titles[actionType] || 'Thực hiện thao tác';
        })()}
        open={actionModalVisible}
        onOk={handleActionSubmit}
        onCancel={handleActionCancel}
        confirmLoading={actionLoading}
        okText="Xác nhận"
        cancelText="Hủy"
        width={isMobile ? '95%' : 500}
        style={isMobile ? { top: 20 } : {}}
      >
        {actionInvoice && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>Hóa đơn:</strong> {actionInvoice.code}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Trạng thái hiện tại:</strong> {' '}
              <Tag color={actionInvoice.status === 'pending' ? 'orange' : 
                          actionInvoice.status === 'completed' ? 'green' : 'red'}>
                {actionInvoice.status === 'pending' ? 'Chưa giải quyết' :
                 actionInvoice.status === 'completed' ? 'Hoàn thành' : 
                 actionInvoice.status}
              </Tag>
            </div>
            <Form form={actionForm} layout="vertical">
              <Form.Item
                name="note"
                label="Ghi chú"
                rules={[
                  { required: false },
                  { max: 500, message: 'Ghi chú không được vượt quá 500 ký tự' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder={`Nhập ghi chú cho việc ${
                    actionType === 'approve' ? 'duyệt' :
                    actionType === 'cancel' ? 'hủy' : 'hủy hoàn thành'
                  } hóa đơn...`}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceImportList;
